import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { prettifyApiError } from "@/shared/api";
import { usePermessi } from "@/shared/auth/permessi";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import {
  aggiornaMagazzino,
  fetchGiorni,
  fetchMagazzini,
  type MagazzinoRiepilogo,
} from "../../ritiri/api";
import { AutomaticoRiga, type AutomaticoPatch } from "../../ritiri/components/AutomaticoRiga";

/** La chiave con cui la pagina Ritiri ricorda il magazzino aperto. */
const CHIAVE_SLOT_RITIRI = "om.ritiri.slot";

type Props = {
  /** Magazzino della pagina Ritiri da cui parte il pacco; null = nessun ritiro SDA. */
  slot: string | null;
  /** Perché questo fornitore non ha un ritiro da OM (solo con ``slot`` null). */
  nota?: string;
  /** Altri fornitori che partono dallo stesso magazzino: l'interruttore vale anche per loro. */
  condiviso?: string;
};

/**
 * Interruttore del ritiro automatico dentro la scheda del fornitore (Davide,
 * 23/10). È lo stesso dato della pagina Ritiri (``pickups.magazzini``):
 * accenderlo qui o là è la stessa cosa, e gli orari restano quelli.
 */
export function RitiroAutomaticoCard({ slot, nota, condiviso }: Props) {
  const { puoScrivere } = usePermessi();
  const [riepilogo, setRiepilogo] = useState<MagazzinoRiepilogo | null>(null);
  const [limite, setLimite] = useState("");
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    if (!slot) return;
    let annullato = false;
    Promise.all([fetchMagazzini(), fetchGiorni()])
      .then(([righe, giorni]) => {
        if (annullato) return;
        setLimite(giorni.limite_in_giornata);
        const r = righe.find((m) => m.slot === slot);
        if (r) setRiepilogo(r);
        else setErrore("Magazzino non trovato nella pagina Ritiri.");
      })
      .catch((e: unknown) => {
        if (!annullato) setErrore(prettifyApiError(e));
      });
    return () => {
      annullato = true;
    };
  }, [slot]);

  async function cambia(patch: AutomaticoPatch) {
    if (!slot) return;
    // L'errore lo mostra la riga stessa accanto all'interruttore: qui si rilancia.
    setRiepilogo(await aggiornaMagazzino(slot, patch));
  }

  function apriInRitiri() {
    // ponytail: la pagina Ritiri legge il magazzino aperto da questa chiave;
    // un parametro nell'URL servirà quando un terzo punto ci vorrà arrivare.
    try {
      if (slot) localStorage.setItem(CHIAVE_SLOT_RITIRI, JSON.stringify(slot));
    } catch {
      // storage bloccato: si apre il magazzino di prima, nessun danno
    }
  }

  return (
    // Non ``Card``: il suo padding fisso si somma a quello della riga sotto,
    // che porta già il suo (è la stessa della scheda in Ritiri).
    <section className="mb-4 rounded-2xl border border-line bg-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-5 pt-4 pb-3">
        <h2 className="font-display text-[16px] font-bold text-ink">Ritiro del corriere</h2>
        {slot && (
          <Link
            to="/ritiri"
            onClick={apriInRitiri}
            className="text-[12.5px] font-bold text-ink underline underline-offset-2 hover:text-accent"
          >
            {riepilogo ? `Apri ${riepilogo.etichetta} in Ritiri` : "Apri in Ritiri"}
          </Link>
        )}
      </div>

      {!slot ? (
        <p className="border-t border-line px-5 py-4 text-[12.5px] text-ink">{nota}</p>
      ) : errore ? (
        <ErrorBanner message={errore} className="mx-5 mb-4" />
      ) : !riepilogo ? (
        <p className="border-t border-line px-5 py-4 text-[12.5px] text-ink">Caricamento…</p>
      ) : (
        <>
          {(riepilogo.segnale_pronto || condiviso || riepilogo.mancanti.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 px-5 pb-3 text-[12.5px] text-ink">
              {riepilogo.segnale_pronto && (
                <span className="font-semibold">{riepilogo.segnale_pronto}.</span>
              )}
              {condiviso && <span>Vale anche per {condiviso}: stesso magazzino.</span>}
              {riepilogo.mancanti.length > 0 && (
                <span className="rounded-full bg-amber-soft px-[7px] py-px text-[11px] font-bold">
                  manca {riepilogo.mancanti.join(" e ")}: completalo in Ritiri
                </span>
              )}
            </div>
          )}
          <AutomaticoRiga
            possibile={riepilogo.automatico_possibile}
            automatico={riepilogo.impostazioni.automatico}
            ora={riepilogo.impostazioni.ora_automatico}
            oraMattina={riepilogo.impostazioni.ora_automatico_mattina}
            limiteInGiornata={limite}
            puo={puoScrivere("spedizioni")}
            onCambia={cambia}
          />
        </>
      )}
    </section>
  );
}
